import { useEffect, useState } from "react";
import { apiFetch } from "@/lib/api";
import {
  clearImpersonation,
  getImpersonationInfo,
  getImpersonationToken,
  type ImpersonationInfo,
} from "@/lib/impersonation";

const BANNER_PX = 40;

/** Persistent "Viewing as <email> — Exit" bar while a super admin views as a user. */
export function ImpersonationBanner() {
  const [info, setInfo] = useState<ImpersonationInfo | null>(() =>
    getImpersonationToken() ? getImpersonationInfo() : null,
  );
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!getImpersonationToken()) {
      return;
    }
    // Confirm with the API (server is the source of truth for the target).
    void apiFetch<{
      impersonation?: { targetEmail?: string | null; targetName?: string | null; expiresAt?: string | null; sessionId?: string | null } | null;
    }>("/auth/session")
      .then((session) => {
        const imp = session.impersonation;
        if (!imp?.targetEmail) {
          clearImpersonation();
          setInfo(null);
          return;
        }
        setInfo({
          sessionId: imp.sessionId ?? "",
          targetEmail: imp.targetEmail,
          targetName: imp.targetName ?? imp.targetEmail,
          expiresAt: imp.expiresAt ?? "",
        });
      })
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    document.body.classList.toggle("prime-impersonating", Boolean(info));
    return () => document.body.classList.remove("prime-impersonating");
  }, [info]);

  if (!info) {
    return null;
  }

  async function exit() {
    setBusy(true);
    try {
      await apiFetch("/admin/impersonation/stop", { method: "POST", body: "{}" });
    } catch {
      /* exit locally even if the API is unreachable */
    } finally {
      clearImpersonation();
      window.location.assign("/settings/admin");
    }
  }

  return (
    <>
      <style>{`
        body.prime-impersonating { padding-top: ${BANNER_PX}px; }
        body.prime-impersonating .h-dvh { height: calc(100dvh - ${BANNER_PX}px); }
        body.prime-impersonating .max-h-dvh { max-height: calc(100dvh - ${BANNER_PX}px); }
        body.prime-impersonating .min-h-screen { min-height: calc(100vh - ${BANNER_PX}px); }
        body.prime-impersonating header.sticky { top: ${BANNER_PX}px; }
      `}</style>
      <div
        role="status"
        aria-live="polite"
        data-testid="impersonation-banner"
        className="fixed inset-x-0 top-0 z-[100] flex items-center justify-center gap-3 bg-amber-500 px-3 text-sm font-semibold text-slate-950 shadow-md"
        style={{ height: BANNER_PX }}
      >
        <span className="truncate">
          Viewing as {info.targetEmail}
          <span className="hidden font-normal sm:inline">
            {" "}— you can do everything they can; every action is logged under your name
          </span>
        </span>
        <a
          href="/view-as/overview"
          data-testid="impersonation-overview-link"
          className="rounded-full border border-slate-950 px-3 py-1 text-xs font-semibold hover:bg-amber-400"
        >
          Everything
        </a>
        <button
          type="button"
          onClick={() => void exit()}
          disabled={busy}
          className="rounded-full bg-slate-950 px-3 py-1 text-xs font-semibold text-white hover:bg-slate-800 disabled:opacity-60"
        >
          {busy ? "Exiting…" : "Exit"}
        </button>
      </div>
    </>
  );
}
